import { useState, useRef, useEffect, useMemo } from 'react';
import { countries, Country, searchCountries, getCountryByDialCode } from '../data/countries';
import { useTheme } from '../contexts/ThemeContext';

interface PhoneInputWithDDIProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  error?: string;
  id?: string;
}

export function PhoneInputWithDDI({
  value,
  onChange,
  placeholder = 'Digite o número',
  className = '',
  error,
  id,
}: PhoneInputWithDDIProps) {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<Country>(countries[0]); // Brasil por padrão
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Parsear o número para extrair o DDI e o número
  useEffect(() => {
    if (value) {
      // Tentar encontrar o país pelo DDI no número
      const country = countries.find(c => value.startsWith(c.dialCode));
      if (country) {
        setSelectedCountry(country);
      } else {
        // Se não encontrar, tentar detectar pelo padrão +55
        const match = value.match(/^(\+\d{1,4})/);
        if (match) {
          const dialCode = match[1];
          const found = getCountryByDialCode(dialCode);
          if (found) {
            setSelectedCountry(found);
          }
        }
      }
    }
  }, []); // Apenas na montagem inicial

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focar no campo de busca quando abrir
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filtrar países baseado na busca (em tempo real)
  const filteredCountries = useMemo(() => {
    // Se não houver busca ou estiver vazio, retornar todos os países
    const trimmedQuery = searchQuery ? searchQuery.trim() : '';
    if (!trimmedQuery) {
      // Retornar uma nova referência de array para garantir que o React detecte a mudança
      return [...countries];
    }
    
    // Usar a função searchCountries que já tem toda a lógica de busca
    // (inclui normalização de acentos, aliases, etc)
    const results = searchCountries(trimmedQuery);
    // Retornar uma nova referência de array para garantir que o React detecte a mudança
    return [...results];
  }, [searchQuery]); // Apenas searchQuery como dependência (countries é constante)

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    setSearchQuery('');
    
    // Se já houver um número, manter apenas a parte local e adicionar o novo DDI
    const currentNumber = value.replace(/^\+\d{1,4}\s*/, ''); // Remove DDI atual se existir
    const newValue = currentNumber ? `${country.dialCode} ${currentNumber}` : country.dialCode;
    onChange(newValue);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    // Sempre combinar o DDI selecionado com o número digitado
    if (selectedCountry) {
      // Se o usuário deletar tudo, manter apenas o DDI
      if (!inputValue) {
        onChange(selectedCountry.dialCode);
        return;
      }
      
      // Combinar DDI selecionado com o número local digitado
      const fullNumber = `${selectedCountry.dialCode} ${inputValue}`;
      onChange(fullNumber);
    } else {
      // Se não houver país selecionado, passar o valor como está
      onChange(inputValue);
    }
  };

  // Extrair apenas o número local (sem DDI) para exibir no input
  const getLocalNumber = () => {
    if (!value) {
      return '';
    }
    
    // Tentar encontrar o DDI do país selecionado no valor
    if (selectedCountry && value.startsWith(selectedCountry.dialCode)) {
      const local = value.slice(selectedCountry.dialCode.length).trim();
      return local;
    }
    
    // Se o valor começar com outro DDI, tentar extrair
    const match = value.match(/^\+\d{1,4}\s*(.+)$/);
    if (match) {
      return match[1];
    }
    
    // Se não tiver DDI, retornar o valor completo
    return value;
  };

  const localNumber = getLocalNumber();
  const displayValue = localNumber || '';

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="flex gap-2">
        {/* Dropdown de seleção de país */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border transition-all min-w-[120px] ${
              theme === 'dark'
                ? 'bg-gray-800 border-gray-600 text-white hover:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
            } ${isOpen ? 'ring-2 ring-blue-500' : ''}`}
            aria-label="Selecionar país"
            aria-expanded={isOpen}
          >
            <span className="text-xl">{selectedCountry.flag}</span>
            <span className="text-sm font-medium">{selectedCountry.dialCode}</span>
            <svg
              className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown menu */}
          {isOpen && (
            <div
              className={`absolute z-50 mt-2 w-80 rounded-lg shadow-2xl border overflow-hidden ${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700'
                  : 'bg-white border-gray-200'
              }`}
              style={{ maxHeight: '400px' }}
            >
              {/* Campo de busca */}
              <div className="p-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                    style={{ color: 'var(--text-muted)' }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setSearchQuery(newValue);
                    }}
                    onKeyDown={(e) => {
                      // Prevenir que o Enter feche o modal ou submeta o formulário
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        // Se houver resultados filtrados, selecionar o primeiro
                        if (filteredCountries.length > 0) {
                          handleCountrySelect(filteredCountries[0]);
                        }
                      }
                      // Prevenir que Escape feche o modal (apenas fechar o dropdown)
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsOpen(false);
                        setSearchQuery('');
                      }
                    }}
                    placeholder="Buscar país (nome, DDI ou sigla, ex: BR, UEA)..."
                    className={`w-full pl-10 pr-3 py-2 rounded-md text-sm ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
                    } border focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                </div>
              </div>

              {/* Lista de países */}
              <div 
                key={`country-list-${searchQuery}-${filteredCountries.length}`}
                className="overflow-y-auto" 
                style={{ maxHeight: '340px' }}
              >
                {(() => {
                  if (filteredCountries.length === 0) {
                    return (
                      <div className="p-4 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                        Nenhum país encontrado
                      </div>
                    );
                  }
                  
                  return filteredCountries.map((country, index) => (
                    <button
                      key={`${country.code}-${searchQuery}-${index}`}
                      type="button"
                      onClick={() => handleCountrySelect(country)}
                      className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-opacity-50 transition-colors text-left ${
                        selectedCountry.code === country.code
                          ? theme === 'dark'
                            ? 'bg-blue-600 bg-opacity-30'
                            : 'bg-blue-50'
                          : theme === 'dark'
                          ? 'hover:bg-gray-700'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-2xl">{country.flag}</span>
                      <div className="flex-1">
                        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          {country.name}
                        </div>
                        <div className="text-xs flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
                          <span>{country.dialCode}</span>
                          <span className="px-1.5 py-0.5 rounded text-xs font-mono bg-opacity-20" style={{ 
                            backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                            color: 'var(--text-muted)'
                          }}>
                            {country.code}
                          </span>
                        </div>
                      </div>
                      {selectedCountry.code === country.code && (
                        <svg
                          className="w-5 h-5 text-blue-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>

        {/* Campo de input de telefone */}
        <div className="flex-1">
          <input
            id={id}
            type="tel"
            value={displayValue}
            onChange={handlePhoneChange}
            placeholder={placeholder}
            className={`input-field w-full ${
              error ? 'border-red-500 focus:ring-red-500' : ''
            }`}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${id}-error` : undefined}
          />
        </div>
      </div>

      {/* Mensagem de erro */}
      {error && (
        <p id={`${id}-error`} className="text-theme-error text-xs font-normal mt-1">
          {error}
        </p>
      )}

      {/* Valor completo para debug (pode remover em produção) */}
      {/* <div className="text-xs text-gray-500 mt-1">Valor completo: {value}</div> */}
    </div>
  );
}

